/**
 * One-shot handoff from the login screen to TenantProvider (gh #872 / #1165).
 *
 * Landing in the right organisation needs two facts that live in different
 * places: WHICH agency the user picked (known only at sign-in) and whether
 * this page load is that sign-in or a later refresh (not otherwise knowable —
 * TenantProvider's hydrate block runs identically in both cases).
 *
 * Without the second fact the two tickets pull in opposite directions:
 *
 *  - #872 / #1165: a fresh login must land in the agency chosen at the picker,
 *    beating any stale tenant left in localStorage by an earlier session.
 *  - #1166's own second QA criterion: switching organisation in-app and then
 *    refreshing must KEEP the switched tenant — which means the persisted
 *    value has to beat the login tenant.
 *
 * So the login tenant wins exactly once, on the load that follows sign-in, and
 * the persisted choice wins on every load after that.
 *
 * sessionStorage, not localStorage: this is scoped to the tab that signed in
 * and must not leak into other tabs or outlive the session. It survives the
 * full document load `finishAuthenticated` performs (gh #1758), which is the
 * only thing it has to survive.
 */

const FRESH_LOGIN_KEY = 'webapp.freshLoginGcipTenantId';

/**
 * Record that this tab just completed a sign-in against `gcipTenantId`.
 * Safe to call with null/undefined — a tenant-less sign-in simply records
 * nothing and TenantProvider falls through to its normal precedence.
 */
export function markFreshLogin(gcipTenantId: string | null | undefined): void {
  if (typeof window === 'undefined' || !gcipTenantId) return;
  try {
    window.sessionStorage.setItem(FRESH_LOGIN_KEY, gcipTenantId);
  } catch {
    // Private mode / blocked storage: the marker is an optimisation, not a
    // correctness requirement. Losing it degrades to the persisted tenant.
  }
}

/**
 * Read and clear the marker. Returns the GCIP tenant signed into if this load
 * follows a sign-in, else null. Clearing is the point — it makes the login
 * tenant authoritative for exactly one page load.
 */
export function consumeFreshLogin(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const value = window.sessionStorage.getItem(FRESH_LOGIN_KEY);
    if (value) window.sessionStorage.removeItem(FRESH_LOGIN_KEY);
    return value;
  } catch {
    return null;
  }
}
